// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@west/App/Store/Reducers";
import LazyPicture from "@west/MobiusOverrides/LazyImage/LazyPicture";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    altText = "altText",
    imageLink = "imageLink",
    customCSS = "customCSS",
    // XNG-Change-WEST-181
    imageMaxWidth = "imageMaxWidth",
    imageMaxHeight = "imageMaxHeight",
    imageMaxWidthOnMobile = "imageMaxWidthOnMobile",
    imageMaxHeightOnMobile = "imageMaxHeightOnMobile",
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    openInNewTab = "openInNewTab",
    disableLazyLoad = "disableLazyLoad",
    sendGoogleAnalyticsEvent = "sendGoogleAnalyticsEvent",
    googleAnalyticsEventName = "googleAnalyticsEventName",
    imageId = "imageId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        // XNG-Change-WEST-181
        [fields.imageMaxWidth]: string;
        [fields.imageMaxHeight]: string;
        [fields.imageMaxWidthOnMobile]: string;
        [fields.imageMaxHeightOnMobile]: string;
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.openInNewTab]: boolean;
        [fields.disableLazyLoad]: boolean;
        [fields.sendGoogleAnalyticsEvent]: boolean;
        [fields.googleAnalyticsEventName]: string;
        [fields.imageId]: string;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
};

// XNG-Change-WEST-181
const styles = imageStyles;

export const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);

    // XNG-Change-WEST-181
    const { isMobileScreen, isTabletScreen, xGoogleAnalytics, product } = useAppSelector(state => {
        // XNG-Changes: get pdp product data if inside pdp for Google Analytics
        let productState;
        const isPDP = getCurrentPage(state).type === "ProductDetailsPage";
        if (isPDP) {
            const location = getLocation(state);
            let productPath =
                getSelectedProductPath(state) ||
                (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
            if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
                productPath = productPath.substring(0, productPath.indexOf("?"));
            }

            productState = getProductStateByPath(state, productPath);
        }
        return {
            isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
            // XNG-Changes: google analytics
            xGoogleAnalytics: (state as CustomAppState).xGoogleAnalytics,
            product: productState?.value || undefined,
        };
    });

    let width = isMobileScreen
        ? fields.imageMaxWidthOnMobile || "768"
        : isTabletScreen
        ? "1024"
        : fields.imageMaxWidth || "1298";
    let height = isMobileScreen ? fields.imageMaxHeightOnMobile : fields.imageMaxHeight;

    width = width.replace(/\D/g, "");
    height = height.replace(/\D/g, "");

    const imageSrc = isMobileScreen
        ? fields.smallImagePath
        : isTabletScreen
        ? fields.mediumImagePath
        : fields.largeImagePath;

    // XNG-Changes: Google Analytics
    const gaImageClickHandler = () => {
        if (xGoogleAnalytics.trackGoogleAnalytics && fields.sendGoogleAnalyticsEvent && url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }
            const googleAnalyticsData = {
                event: fields.googleAnalyticsEventName,
                element_id: fields.imageId,
                item_id: product ? product.id : "",
                item_name: product ? product.productTitle : "",
                destination: url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    const image = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            disableLazyLoad={fields.disableLazyLoad}
        />
    );

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="img-wrapper" {...styles.wrapper}>
                {url ? (
                    <Link
                        className="img-link"
                        href={url}
                        onClick={gaImageClickHandler}
                        {...styles.imageLink}
                        target={fields.openInNewTab ? "_blank" : "_self"}
                    >
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            // XNG-Change-WEST-181
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            // XNG-Change-WEST-181
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // XNG-Changes: google analytics
            {
                name: fields.sendGoogleAnalyticsEvent,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.googleAnalyticsEventName,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                tab: basicTab,
                isVisible: item => item.fields.sendGoogleAnalyticsEvent,
            },
            {
                name: fields.imageId,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                tab: basicTab,
                isVisible: item => item.fields.sendGoogleAnalyticsEvent,
            },
            // XNG-Change-WEST-181
            {
                name: fields.disableLazyLoad,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.imageMaxWidth,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 200px or any width style",
                validate: (value: string | null, item: HasFields) => {
                    return !value ? null : CSS.supports("width", value) ? null : `Invalid css width value`;
                },
                tab: advancedTab,
            },
            {
                name: fields.imageMaxHeight,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 50px or any height style",
                validate: (value: string | null, item: HasFields) => {
                    return !value ? null : CSS.supports("height", value) ? null : `Invalid css height value`;
                },
                tab: advancedTab,
            },
            {
                name: fields.imageMaxWidthOnMobile,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 200px or any width style",
                validate: (value: string | null, item: HasFields) => {
                    return !value ? null : CSS.supports("width", value) ? null : `Invalid css width value`;
                },
                tab: advancedTab,
            },
            {
                name: fields.imageMaxHeightOnMobile,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 50px or any height style",
                validate: (value: string | null, item: HasFields) => {
                    return !value ? null : CSS.supports("height", value) ? null : `Invalid css height value`;
                },
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
